import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  getCategories,
  createOffer,
  getOffers,
  deleteOffer,
  editOffer,
  toggleOfferStatus,
  createSubCategory,
  createRule,
  getRules,
  getOfferById,
  getNearestOffersByMerchant,
  getCategoryById,
  getOffersByMerchant,
  approveOrRejectResources,
  getNonDraftedOffers,
  getOfferRedemptionCodeCounts,
  updateOfferCodes,
  getOfferCodeDetails,
  deleteSubCategory,
  getAllCategories,
  updateOfferRank,
  getGroupedRules,
} from "../../services/offerService";
import toast from "../../utils/toast";

const initialState = {
  categories: null,
  createdOffer: null,
  offers: null,
  deletedOffer: null,
  updatedOffer: null,
  toggledOffer: null,
  newSubCategory: null,
  newRule: null,
  allRules: null,
  offerById: null,
  merchantDetails: null,
  categoryById: null,
  offersByMerchant: null,
  approveOrReject: null,
  nonDraftedoffers: null,
  // New offer code management state
  offerRedemptionCodeCounts: null,
  offerCodeDetails: null,
  updatedOfferCodes: null,
  groupedRules: null,
  loading: false,
};

const offerSlice = createSlice({
  name: "offer",
  initialState: initialState,
  reducers: {
    resetOfferState: (state, action) => {
      state.createdOffer = null;
      state.updatedOffer = null;
      state.newSubCategory = null;
      state.newRule = null;
      state.offerById = null;
      state.loading = false;
      state.offerRedemptionCodeCounts = null;
      state.offerCodeDetails = null;
      state.updatedOfferCodes = null;
    },
    setCreatedOffer: (state, action) => {
      state.createdOffer = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCategories?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.categories = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getAllCategories?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.categories = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(createOffer?.fulfilled, (state, action) => {
      console.log("createOffer fulfilled - action.payload:", action?.payload);
      console.log("createOffer fulfilled - action.payload.data:", action?.payload?.data);
      if (action?.payload?.success === true) {
        state.loading = false;
        // Preserve offerCodes from the request payload since backend doesn't return them
        const requestPayload = action?.meta?.arg;
        console.log("createOffer fulfilled - request payload:", requestPayload);

        state.createdOffer = {
          ...(state.createdOffer || {}),
          ...action?.payload?.data,
          offerCodes: requestPayload?.offerCodes || action?.payload?.data?.offerCodes || state.createdOffer?.offerCodes
        };
        console.log("createOffer fulfilled - updated state.createdOffer:", state.createdOffer);
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getOffers?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.offers = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(editOffer?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.updatedOffer = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(deleteOffer?.fulfilled, (state, action) => {
      // Do not show a toast here; just update state
      state.loading = false;
      if (action?.payload?.success === true) {
        state.deletedOffer = action?.payload?.data;
      }
    });

    builder.addCase(toggleOfferStatus?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.toggledOffer = action?.payload?.data;
        toast.success(action?.payload?.message, {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(createSubCategory?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.newSubCategory = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(deleteSubCategory?.fulfilled, (state, action) => {
      state.loading = false;
      if (action?.payload?.success === true) {
        toast.success(action?.payload?.message || "Subcategory deleted successfully");
      } else {
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(createRule?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.newRule = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getRules?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.allRules = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getGroupedRules?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.groupedRules = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getOfferById?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.offerById = action?.payload?.data;
        // Keep createdOffer in sync during edit flow initialization, but merge to preserve session state
        state.createdOffer = {
          ...(state.createdOffer || {}),
          ...action?.payload?.data
        };
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getNearestOffersByMerchant?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.merchantDetails = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getCategoryById?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.categoryById = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getOffersByMerchant?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.offersByMerchant = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(approveOrRejectResources?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.approveOrReject = action?.payload?.data;
        const requestedStatus = action?.meta?.arg?.status;
        const fallbackMsg =
          requestedStatus === "approved"
            ? "Offer approved successfully!"
            : requestedStatus === "declined"
              ? "Offer declined successfully!"
              : "Status updated successfully!";
        const toastMessage = action?.payload?.message || fallbackMsg;
        toast.success(toastMessage, {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    // New offer code management reducers
    builder.addCase(getOfferRedemptionCodeCounts?.fulfilled, (state, action) => {
      console.log("offerSlice - getOfferRedemptionCodeCounts fulfilled - action.payload:", action?.payload);

      if (action?.payload?.success === true) {
        state.loading = false;
        state.offerRedemptionCodeCounts = action?.payload?.data;
        console.log("offerSlice - Updated offerRedemptionCodeCounts:", state.offerRedemptionCodeCounts);
      } else {
        state.loading = false;
        console.log("offerSlice - getOfferRedemptionCodeCounts failed - payload:", action?.payload);
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(updateOfferCodes?.fulfilled, (state, action) => {
      console.log("offerSlice - updateOfferCodes fulfilled - action.payload:", action?.payload);
      console.log("offerSlice - updateOfferCodes fulfilled - action.payload.success:", action?.payload?.success);

      if (action?.payload?.success === true) {
        state.loading = false;
        state.updatedOfferCodes = action?.payload?.data;

        // Clear the existing redemption code counts to force a refresh
        state.offerRedemptionCodeCounts = null;
        state.offerCodeDetails = null;

        console.log("offerSlice - Cleared offerRedemptionCodeCounts and offerCodeDetails");

        toast.success(action?.payload?.message || "Codes added successfully", {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      } else {
        state.loading = false;
        console.log("offerSlice - updateOfferCodes failed - payload:", action?.payload);
        toast.error(action?.payload?.message || "Failed to add codes");
      }
    });

    builder.addCase(getOfferCodeDetails?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.offerCodeDetails = action?.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(getNonDraftedOffers?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        state.nonDraftedoffers = action?.payload?.data;
        toast.success(action?.payload?.data, {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });
    builder.addCase(updateOfferRank?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.loading = false;
        // Update the offer in the nonDraftedoffers list if it exists
        if (state.nonDraftedoffers && Array.isArray(state.nonDraftedoffers)) {
          const index = state.nonDraftedoffers.findIndex(
            (off) => off._id === action.meta.arg.offerId
          );
          if (index !== -1) {
            state.nonDraftedoffers[index] = {
              ...state.nonDraftedoffers[index],
              ...action.meta.arg, // contains isHot, isFeatured, isPopular
            };
          }
        }
        // Also update offerById if it matches
        if (state.offerById?._id === action.meta.arg.offerId) {
          state.offerById = {
            ...state.offerById,
            ...action.meta.arg,
          };
        }
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder
      .addMatcher(
        isAnyOf(
          getCategories?.pending,
          createOffer?.pending,
          getOffers?.pending,
          editOffer?.pending,
          deleteOffer?.pending,
          toggleOfferStatus?.pending,
          createSubCategory?.pending,
          createRule?.pending,
          getRules?.pending,
          getOfferById?.pending,
          getNearestOffersByMerchant?.pending,
          getCategoryById?.pending,
          getOffersByMerchant?.pending,
          approveOrRejectResources?.pending,
          getNonDraftedOffers?.pending,
          getOfferRedemptionCodeCounts?.pending,
          updateOfferCodes?.pending,
          getOfferCodeDetails?.pending,
          deleteSubCategory?.pending,
          getAllCategories?.pending,
          updateOfferRank?.pending,
          getGroupedRules?.pending
        ),
        (state, action) => {
          // Do not trigger global loading overlay for silent fetches (e.g., navbar live search)
          if (action?.meta?.arg?.silent === true) return;
          state.loading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          getCategories?.rejected,
          createOffer?.rejected,
          getOffers?.rejected,
          editOffer?.rejected,
          deleteOffer?.rejected,
          toggleOfferStatus?.rejected,
          createSubCategory?.rejected,
          createRule?.rejected,
          getRules?.rejected,
          getOfferById?.rejected,
          getNearestOffersByMerchant?.rejected,
          getCategoryById?.rejected,
          getOffersByMerchant?.rejected,
          approveOrRejectResources?.rejected,
          getNonDraftedOffers?.rejected,
          getOfferRedemptionCodeCounts?.rejected,
          updateOfferCodes?.rejected,
          getOfferCodeDetails?.rejected,
          deleteSubCategory?.rejected,
          getAllCategories?.rejected,
          updateOfferRank?.rejected,
          getGroupedRules?.rejected
        ),
        (state, action) => {
          state.loading = false;
          // Allow callers to silence error toasts (e.g., fast search typing)
          if (action?.meta?.arg?.silent === true) {
            return;
          }
          const errorMessage = action?.payload?.message ||
            action?.error?.message ||
            "An error occurred while processing your request";
          // Hide "Merchant ID is required" error messages
          if (errorMessage && /merchant id is required/i.test(errorMessage)) {
            return; // Don't show toast for this error
          }
          // Deduplicate toasts within a short window to prevent stacking
          if (typeof window !== 'undefined') {
            const now = Date.now();
            const last = window.__LAST_ERROR_TOAST__ || { msg: null, at: 0 };
            if (last.msg !== errorMessage || now - last.at > 4000) {
              toast.error(errorMessage);
              window.__LAST_ERROR_TOAST__ = { msg: errorMessage, at: now };
            }
          } else {
            toast.error(errorMessage);
          }
        }
      );
  },
});

export const { resetOfferState } = offerSlice?.actions;
export default offerSlice?.reducer;
